import { ChevronDown, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWrapper } from "src/helpers";
import {
  AVG_VOLUME_OPTIONS,
  FIBONACCI_LEVELS_OPTIONS,
  MOVING_AVERAGES_OPTIONS,
  SCREENER_FILTERS,
  ScreenerFilterDefinition,
  ScreenerFilterState,
  ScreenerFilterValue,
} from "./screenerFilter";

interface FilterOption {
  label: string;
  value: string;
  sector?: string;
}

interface ScreenerFilterBarProps {
  filterState: ScreenerFilterState;
  onFilterChange: (state: ScreenerFilterState) => void;
}

function isFilterActive(
  filter: ScreenerFilterDefinition,
  state: ScreenerFilterValue | undefined,
): boolean {
  if (!state) return false;
  if (state.type === "toggle") return true;
  if (state.type === "userInput") return state.value.trim().length > 0;
  if (state.type === "select") return state.value.length > 0;
  if (state.type === "selectWithInput" || state.type === "selectWithSelect")
    return state.value.trim().length > 0 && !!state.selection;
  return false;
}

function getFilterSummary(
  filter: ScreenerFilterDefinition,
  state: ScreenerFilterValue | undefined,
  options: FilterOption[] = [],
): string | null {
  if (!state || !isFilterActive(filter, state)) return null;
  if (state.type === "toggle") return null;
  if (state.type === "userInput") {
    const suffix = filter.unit ? ` ${filter.unit}` : "";
    if (state.operator === "between") {
      return `${state.value} - ${state.maxValue}${suffix}`;
    }
    const opSign = state.operator === "greater_than_equal" ? "≥" : "≤";
    return `${opSign} ${state.value}${suffix}`;
  }
  if (state.type === "select") {
    if (state.value.length === 1) {
      const selectedOption = options.find((o) => o.value === state.value[0]);
      // Remove the stock count suffix for the pill summary if it's a universe
      let label = selectedOption?.label ?? state.value[0];
      if (filter.optionsKey === "universes" && label.includes(" (")) {
        label = label.split(" (")[0];
      }
      return label;
    }
    return `${state.value.length} selected`;
  }
  if (state.type === "selectWithInput" || state.type === "selectWithSelect") {
    const suffix = filter.unit ? ` ${filter.unit}` : "";
    const selectedOption = options.find((o) => o.value === state.selection);
    return `${selectedOption?.label ?? state.selection}: ${state.value}${suffix}`;
  }
  return null;
}

interface FilterPillProps {
  filter: ScreenerFilterDefinition;
  value: ScreenerFilterValue | undefined;
  options: FilterOption[];
  isOpen: boolean;
  onToggleOpen: () => void;
  onChange: (value: ScreenerFilterValue | undefined) => void;
  onClose: () => void;
}

function FilterPill({
  filter,
  value,
  options,
  isOpen,
  onToggleOpen,
  onChange,
  onClose,
}: FilterPillProps) {
  const pillRef = useRef<HTMLDivElement>(null);
  const [draftInput, setDraftInput] = useState("");
  const [draftMaxInput, setDraftMaxInput] = useState("");
  const [draftSelection, setDraftSelection] = useState("");
  const [draftOperator, setDraftOperator] = useState<
    "greater_than_equal" | "less_than_equal" | "between"
  >("greater_than_equal");
  const [dropdownPos, setDropdownPos] = useState<"left" | "right">("left");

  const active = isFilterActive(filter, value);
  const summary = getFilterSummary(filter, value, options);

  useEffect(() => {
    if (!isOpen) return;

    if (pillRef.current) {
      const rect = pillRef.current.getBoundingClientRect();
      setTimeout(() => {
        if (window.innerWidth - rect.left < 300) {
          setDropdownPos("right");
        } else {
          setDropdownPos("left");
        }
      }, 0);
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handlePillClick = () => {
    if (filter.inputType === "toggle") {
      if (active) {
        onChange(undefined);
      } else {
        onChange({ type: "toggle" });
      }
      return;
    }
    if (!isOpen) {
      if (value?.type === "userInput") {
        setDraftInput(value.value);
        setDraftMaxInput(value.maxValue ?? "");
        setDraftOperator(value.operator);
      } else if (
        value?.type === "selectWithInput" ||
        value?.type === "selectWithSelect"
      ) {
        setDraftInput(value.value);
        setDraftSelection(value.selection);
      } else {
        setDraftInput(filter.inputType === "selectWithSelect" ? "5" : "");
        setDraftMaxInput("");
        setDraftOperator("greater_than_equal");
        if (options.length > 0) setDraftSelection(options[0].value);
      }
    }
    onToggleOpen();
  };

  const applyUserInput = () => {
    const trimmed = draftInput.trim();
    if (!trimmed) {
      onChange(undefined);
    } else {
      if (
        filter.inputType === "selectWithInput" ||
        filter.inputType === "selectWithSelect"
      ) {
        onChange({
          type: filter.inputType,
          selection: draftSelection,
          value: trimmed,
        });
      } else {
        onChange({
          type: "userInput",
          operator: draftOperator,
          value: trimmed,
          maxValue:
            draftOperator === "between" ? draftMaxInput.trim() : undefined,
        });
      }
    }
    onClose();
  };

  const toggleSelectOption = (optionValue: string) => {
    const current = value?.type === "select" ? value.value : ([] as string[]);
    const next = current.includes(optionValue)
      ? current.filter((v) => v !== optionValue)
      : [...current, optionValue];
    if (next.length === 0) {
      onChange(undefined);
    } else {
      onChange({ type: "select", value: next });
    }
  };

  const clearFilter = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    onClose();
  };

  return (
    <div ref={pillRef} className="relative">
      <div
        className={`flex items-center border rounded px-2.5 py-1 text-xs whitespace-nowrap transition-colors ${
          active
            ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
            : "border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400"
        } ${filter.inputType === "toggle" ? "cursor-pointer" : ""}`}
      >
        <button
          type="button"
          onClick={handlePillClick}
          className="flex items-center cursor-pointer"
        >
          <span>
            {filter.label}
            {summary ? (
              <span className="ml-1 font-medium opacity-80">: {summary}</span>
            ) : null}
          </span>
          {filter.inputType !== "toggle" && !active && (
            <ChevronDown className="w-3 h-3 ml-1.5 text-gray-400 shrink-0" />
          )}
        </button>
        {active && (
          <button
            type="button"
            onClick={clearFilter}
            className="cursor-pointer ml-1.5 shrink-0 opacity-60 hover:opacity-100"
            aria-label={`Clear ${filter.label} filter`}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {isOpen && filter.inputType !== "toggle" && (
        <div
          className={`absolute ${
            dropdownPos === "right" ? "right-0" : "left-0"
          } top-full mt-1 z-50 min-w-[200px] max-w-[280px] bg-white dark:bg-[#1E1E2D] border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2`}
        >
          {filter.inputType === "userInput" && (
            <div className="flex flex-col gap-2">
              <select
                value={draftOperator}
                onChange={(e) =>
                  setDraftOperator(
                    e.target.value as
                      | "greater_than_equal"
                      | "less_than_equal"
                      | "between",
                  )
                }
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white outline-none"
              >
                <option value="greater_than_equal">
                  Greater than or equals to
                </option>
                <option value="less_than_equal">Less than or equals to</option>
                <option value="between">Between</option>
              </select>
              {draftOperator === "between" ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={draftInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^-?\d*\.?\d{0,2}$/.test(val)) {
                        setDraftInput(val);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyUserInput();
                    }}
                    placeholder="Min"
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white"
                    autoFocus
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={draftMaxInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^-?\d*\.?\d{0,2}$/.test(val)) {
                        setDraftMaxInput(val);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyUserInput();
                    }}
                    placeholder="Max"
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white"
                  />
                </div>
              ) : (
                <input
                  type="text"
                  inputMode="decimal"
                  value={draftInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^-?\d*\.?\d{0,2}$/.test(val)) {
                      setDraftInput(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyUserInput();
                  }}
                  placeholder={filter.placeholder ?? "Enter value"}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white"
                  autoFocus
                />
              )}
              <button
                type="button"
                onClick={applyUserInput}
                className="cursor-pointer w-full py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                Apply
              </button>
            </div>
          )}

          {filter.inputType === "selectWithInput" && (
            <div className="flex flex-col gap-2">
              <select
                value={draftSelection}
                onChange={(e) => setDraftSelection(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white outline-none"
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                inputMode="decimal"
                value={draftInput}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^-?\d*\.?\d{0,2}$/.test(val)) {
                    setDraftInput(val);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyUserInput();
                }}
                placeholder={filter.placeholder ?? "Enter value"}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white"
                autoFocus
              />
              <button
                type="button"
                onClick={applyUserInput}
                className="cursor-pointer w-full py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                Apply
              </button>
            </div>
          )}

          {filter.inputType === "selectWithSelect" && (
            <div className="flex flex-col gap-2">
              <select
                value={draftSelection}
                onChange={(e) => setDraftSelection(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white outline-none"
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={draftInput}
                onChange={(e) => setDraftInput(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-black dark:text-white outline-none"
              >
                <option value="" disabled>
                  Select value
                </option>
                {["5", "10", "15", "20"].map((val) => (
                  <option key={val} value={val}>
                    {val} days
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={applyUserInput}
                className="cursor-pointer w-full py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                Apply
              </button>
            </div>
          )}

          {filter.inputType === "select" && (
            <div className="flex flex-col">
              {options.length > 0 && (
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-100 dark:border-gray-800 mb-1">
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        type: "select",
                        value: options.map((o) => o.value),
                      })
                    }
                    className="cursor-pointer text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange(undefined)}
                    className="cursor-pointer text-xs text-red-500 hover:text-red-600 dark:text-red-400 font-medium"
                  >
                    Reset
                  </button>
                </div>
              )}
              <div className="max-h-48 overflow-y-auto flex flex-col gap-0.5">
                {options.length === 0 ? (
                  <p className="text-xs text-gray-500 px-1 py-2">No options</p>
                ) : (
                  options.map((opt) => {
                    const checked =
                      value?.type === "select" &&
                      value.value.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleSelectOption(opt.value)}
                        className="flex items-center gap-2 px-2 py-1.5 text-xs text-left rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          readOnly
                          className="pointer-events-none"
                        />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ScreenerFilterBar({
  filterState,
  onFilterChange,
}: ScreenerFilterBarProps) {
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const [sectorOptions, setSectorOptions] = useState<FilterOption[]>([]);
  const [industryOptions, setIndustryOptions] = useState<FilterOption[]>([]);
  const [indexOptions, setIndexOptions] = useState<FilterOption[]>([]);
  const [universeOptions, setUniverseOptions] = useState<FilterOption[]>([]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await fetchWrapper.get("/api/screener/filters");
        const data = res?.data || {};

        if (data.industries) {
          setIndustryOptions(
            data.industries.map((i: { industry: string; sector: string }) => ({
              label: i.industry,
              value: i.industry,
              sector: i.sector,
            })),
          );
        }

        if (data.sectors) {
          setSectorOptions(
            data.sectors.map((s: { sector: string }) => ({
              label: s.sector,
              value: s.sector,
            })),
          );
        }

        if (data.indices) {
          setIndexOptions(
            data.indices.map((idx: { indices_name: string }) => ({
              label: idx.indices_name,
              value: idx.indices_name,
            })),
          );
        }

        if (data.universes) {
          setUniverseOptions(
            data.universes.map(
              (u: { id: string; name: string; stocks_count: number }) => ({
                label: `${u.name} (${u.stocks_count} stocks)`,
                value: u.id,
              }),
            ),
          );
        }
      } catch {
        /* filters load optionally */
      }
    };
    void loadFilters();
  }, []);

  const getOptionsForFilter = useCallback(
    (filter: ScreenerFilterDefinition): FilterOption[] => {
      if (filter.optionsKey === "sectors") return sectorOptions;
      if (filter.optionsKey === "industries") {
        const selectedSectorsState = filterState["sectors"];
        if (
          selectedSectorsState &&
          selectedSectorsState.type === "select" &&
          selectedSectorsState.value.length > 0
        ) {
          const selectedSectors = selectedSectorsState.value;
          return industryOptions.filter(
            (opt) => opt.sector && selectedSectors.includes(opt.sector),
          );
        }
        return industryOptions;
      }
      if (filter.optionsKey === "indices") return indexOptions;
      if (filter.optionsKey === "universes") return universeOptions;
      if (filter.optionsKey === "fibonacci_levels")
        return FIBONACCI_LEVELS_OPTIONS;
      if (filter.optionsKey === "average_volume") return AVG_VOLUME_OPTIONS;
      if (filter.optionsKey === "moving_averages")
        return MOVING_AVERAGES_OPTIONS;
      return [];
    },
    [
      sectorOptions,
      industryOptions,
      indexOptions,
      universeOptions,
      filterState,
    ],
  );

  const updateFilter = (id: string, value: ScreenerFilterValue | undefined) => {
    const next = { ...filterState };
    if (value === undefined) {
      delete next[id];
    } else {
      next[id] = value;
    }

    if (id === "sectors") {
      const selectedSectorsState = next["sectors"];
      const industriesState = next["industries"];

      if (industriesState && industriesState.type === "select") {
        if (
          !selectedSectorsState ||
          selectedSectorsState.type !== "select" ||
          selectedSectorsState.value.length === 0
        ) {
          delete next["industries"];
        } else {
          const selectedSectors = selectedSectorsState.value;
          const validIndustries = industryOptions
            .filter((opt) => opt.sector && selectedSectors.includes(opt.sector))
            .map((opt) => opt.value);

          const nextIndustries = industriesState.value.filter((ind) =>
            validIndustries.includes(ind),
          );

          if (nextIndustries.length === 0) {
            delete next["industries"];
          } else {
            next["industries"] = { type: "select", value: nextIndustries };
          }
        }
      }
    }

    onFilterChange(next);
  };

  return (
    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
      <div className="flex flex-wrap gap-x-2 gap-y-2 items-center">
        {SCREENER_FILTERS.filter(
          (filter) => filter.id !== "universe_id" || universeOptions.length > 0,
        ).map((filter) => (
          <FilterPill
            key={filter.id}
            filter={filter}
            value={filterState[filter.id]}
            options={getOptionsForFilter(filter)}
            isOpen={openFilterId === filter.id}
            onToggleOpen={() =>
              setOpenFilterId((prev) => (prev === filter.id ? null : filter.id))
            }
            onChange={(val) => updateFilter(filter.id, val)}
            onClose={() => setOpenFilterId(null)}
          />
        ))}

        {/* <div className="flex items-center border border-dashed border-gray-400 dark:border-gray-500 rounded px-2 py-1 text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <Plus className="w-3 h-3 text-gray-500" />
        </div> */}
        <div className="flex items-center gap-3 ml-auto pl-2">
          {/* <button
            type="button"
            onClick={handleSelectAll}
            className="cursor-pointer px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm transition-colors"
          >
            Select All Filters
          </button> */}
          <button
            type="button"
            onClick={() => onFilterChange({})}
            className="cursor-pointer px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-[#1a1d24] border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md shadow-sm transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
}
